import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { BotMessageSquare, Check, Copy, RefreshCw } from "lucide-react";

import ChatPromptActions from "./ChatPromptActions";
import ChatQuillReadOnly from "./ChatQuillReadOnly";
import ContextUsedPanel from "./ContextUsedPanel";
import MessageSuggestedPrompts from "./MessageSuggestedPrompts";
import ToolCallTracePanel from "./ToolCallTracePanel";
import type { ChatMessageProps, ManagingPromptAction } from "../types/chat.types";
import {
  buildQuillDeltaFromPlainText,
  resolveMessageContentDelta,
} from "../utils/buildQuillDelta";
import {
  deltaHasInlineCrmActions,
  openManagingPromptAction,
} from "../utils/managingPromptAction";
import "./chat-quill.css";

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isStreaming = false,
  onRegenerate,
  onCopy,
  record,
  showToolTrace = true,
  promptCatalog,
  onSelectSuggestedPrompt,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);

  const handleCrmActionClick = useCallback(
    (action: ManagingPromptAction) => {
      if (!record?.recordId) {
        return;
      }
      openManagingPromptAction(dispatch, record, action);
    },
    [dispatch, record]
  );

  if (message.role === "system") {
    return (
      <div className="my-2 text-center text-xs italic text-gray-500">
        {message.error ? (
          <span className="text-red-600">{message.content}</span>
        ) : (
          message.content
        )}
      </div>
    );
  }

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="ml-auto max-w-[85%] rounded-2xl bg-gray-100 px-4 py-2 text-sm text-gray-900">
          {message.promptName ? (
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              {message.promptName}
            </div>
          ) : null}
          <ChatQuillReadOnly
            contentDelta={buildQuillDeltaFromPlainText(message.content)}
          />
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      onCopy?.();
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const assistantDelta = resolveMessageContentDelta(
    message.content,
    message.contentDelta,
    message.actions
  );

  const hasToolTrace = showToolTrace && (message.toolCalls?.length ?? 0) > 0;
  const showContextPanel =
    !hasToolTrace && Boolean(message.metadata?.contextSummary);
  const inlineActionsInDelta = deltaHasInlineCrmActions(message.contentDelta);
  const showFooterActions =
    (message.actions?.length ?? 0) > 0 && !inlineActionsInDelta;

  return (
    <div className="group flex w-full gap-3">
      <div
        className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600"
        aria-hidden="true"
      >
        <BotMessageSquare className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="mr-auto w-full rounded-2xl bg-white px-4 py-3 text-sm text-gray-900"
          aria-live={isStreaming ? "polite" : "off"}
        >
          {hasToolTrace ? <ToolCallTracePanel toolCalls={message.toolCalls!} /> : null}
          <ChatQuillReadOnly
            contentDelta={assistantDelta}
            isStreaming={isStreaming}
            record={record}
            onCrmActionClick={handleCrmActionClick}
          />
        </div>

        {showContextPanel ? <ContextUsedPanel metadata={message.metadata} /> : null}

        {showFooterActions ? (
          <ChatPromptActions actions={message.actions!} record={record} />
        ) : null}

        {onSelectSuggestedPrompt && message.metadata?.suggestedPrompts?.length ? (
          <MessageSuggestedPrompts
            suggestions={message.metadata.suggestedPrompts}
            promptCatalog={promptCatalog}
            onSelect={onSelectSuggestedPrompt}
            messageContent={message.content}
          />
        ) : null}

        {!isStreaming && message.content ? (
          <div className="mt-1 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label={t("Copy message")}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t("Copied") : t("Copy")}
            </button>
            {onRegenerate ? (
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label={t("Regenerate response")}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t("Regenerate")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChatMessage;
